import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  UserIcon,
  CheckCircleIcon,
  ClockIcon,
  EditIcon,
  ExclamationTriangleIcon,
  SignOutIcon,
  HomeIcon,
  CreditCardIcon,
  CalendarIcon
} from '../utils/IconWrapper';
import { useAuth } from '../utils/AuthContext';
import { getUserBookings, Booking } from '../utils/supabaseExtended';
import { formatPrice } from '../utils/config';
import Layout from '../components/layout/Layout';

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
`;

const DashboardHeader = styled.header`
  background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  color: white;
`;

const WelcomeTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
`;

const WelcomeSubtitle = styled.p`
  font-size: 1.1rem;
  opacity: 0.9;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const CardTitle = styled.h2`
  color: #0d9488;
  margin: 0;
  font-size: 1.25rem;
`;

const CardIcon = styled.div`
  color: #0d9488;
  font-size: 1.25rem;
`;

const ProfileStatus = styled.div<{ verified?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background: ${props => props.verified ? '#ecfdf5' : '#fef3c7'};
  border: 1px solid ${props => props.verified ? '#10b981' : '#f59e0b'};
  margin-bottom: 1rem;
`;

const StatusIcon = styled.div<{ verified?: boolean }>`
  color: ${props => props.verified ? '#10b981' : '#f59e0b'};
`;

const StatusText = styled.span<{ verified?: boolean }>`
  color: ${props => props.verified ? '#065f46' : '#92400e'};
  font-weight: 500;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: #6b7280;
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: #374151;
  font-weight: 600;
`;

const ActionButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(13, 148, 136, 0.4);
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #ef4444;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #dc2626;
    transform: translateY(-2px);
  }
`;

const BookingCard = styled.div`
  background: #f8fafc;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  border: 1px solid #e2e8f0;
`;

const BookingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const BookingTitle = styled.h3`
  color: #0d9488;
  margin: 0;
`;

const BookingStatus = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  
  ${props => {
    switch (props.status) {
      case 'confirmed':
        return 'background: #ecfdf5; color: #065f46; border: 1px solid #10b981;';
      case 'pending':
        return 'background: #fef3c7; color: #92400e; border: 1px solid #f59e0b;';
      case 'cancelled':
        return 'background: #fef2f2; color: #991b1b; border: 1px solid #ef4444;';
      default:
        return 'background: #f3f4f6; color: #374151; border: 1px solid #d1d5db;';
    }
  }}
`;

const BookingDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 3rem;
  color: #6b7280;
`;

const Dashboard: React.FC = () => {
  const { user, profile, loading, logout } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/register');
      return;
    }

    const fetchBookings = async () => {
      try {
        const userBookings = await getUserBookings();
        setBookings(userBookings);
      } catch (error) {
        console.error('Error fetching bookings:', error);
      } finally {
        setLoadingBookings(false);
      }
    };

    if (user) {
      fetchBookings();
    }
  }, [user, loading, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return <Layout><div>Caricamento...</div></Layout>;
  }

  if (!user) {
    return null;
  }

  const getVerificationStatus = () => {
    if (!profile) return { verified: false, message: 'Profilo in caricamento...' };
    
    const emailVerified = profile.email_verified;
    const phoneVerified = profile.phone_verified;
    const identityVerified = profile.identity_verified;
    
    if (emailVerified && phoneVerified && identityVerified) {
      return { verified: true, message: 'Account completamente verificato' };
    }
    
    const pending = [];
    if (!emailVerified) pending.push('email');
    if (!phoneVerified) pending.push('telefono');
    if (!identityVerified) pending.push('identità');
    
    return { 
      verified: false, 
      message: `Verifiche mancanti: ${pending.join(', ')}` 
    };
  };

  const verificationStatus = getVerificationStatus();

  return (
    <Layout>
      <DashboardContainer>
        <DashboardHeader>
          <WelcomeTitle>
            Ciao {profile?.full_name || user.email?.split('@')[0] || 'Utente'}! 👋
          </WelcomeTitle>
          <WelcomeSubtitle>
            Benvenuto nella tua dashboard J-O-H. Gestisci il tuo profilo e le tue prenotazioni.
          </WelcomeSubtitle>
        </DashboardHeader>

        <DashboardGrid>
          <Sidebar>
            {/* Profilo Card */}
            <Card>
              <CardHeader>
                <CardIcon><UserIcon /></CardIcon>
                <CardTitle>Il Tuo Profilo</CardTitle>
              </CardHeader>
              
              <ProfileStatus verified={verificationStatus.verified}>
                <StatusIcon verified={verificationStatus.verified}>
                  {verificationStatus.verified ? <CheckCircleIcon /> : <ClockIcon />}
                </StatusIcon>
                <StatusText verified={verificationStatus.verified}>
                  {verificationStatus.message}
                </StatusText>
              </ProfileStatus>

              <InfoRow>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>{user.email}</InfoValue>
              </InfoRow>
              
              {profile?.phone_number && (
                <InfoRow>
                  <InfoLabel>Telefono</InfoLabel>
                  <InfoValue>{profile.phone_number}</InfoValue>
                </InfoRow>
              )}
              
              {profile?.nationality && (
                <InfoRow>
                  <InfoLabel>Nazionalità</InfoLabel>
                  <InfoValue>{profile.nationality}</InfoValue>
                </InfoRow>
              )}

              <InfoRow>
                <InfoLabel>Fase Registrazione</InfoLabel>
                <InfoValue>{profile?.registration_phase || 1}/3</InfoValue>
              </InfoRow>

              <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <ActionButton to="/profile/edit">
                  <EditIcon />
                  Modifica Profilo
                </ActionButton>
                
                {!verificationStatus.verified && (
                  <ActionButton to="/profile/verify">
                    <ExclamationTriangleIcon />
                    Completa Verifiche
                  </ActionButton>
                )}
                
                <LogoutButton onClick={handleLogout}>
                  <SignOutIcon />
                  Logout
                </LogoutButton>
              </div>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardIcon><HomeIcon /></CardIcon>
                <CardTitle>Azioni Rapide</CardTitle>
              </CardHeader>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <ActionButton to="/accommodations">
                  <HomeIcon />
                  Prenota Alloggio
                </ActionButton>
                
                <ActionButton to="/services">
                  <CreditCardIcon />
                  Esplora Servizi
                </ActionButton>
              </div>
            </Card>
          </Sidebar>

          <MainContent>
            {/* Prenotazioni */}
            <Card>
              <CardHeader>
                <CardIcon><CalendarIcon /></CardIcon>
                <CardTitle>Le Tue Prenotazioni</CardTitle>
              </CardHeader>

              {loadingBookings ? (
                <LoadingContainer>Caricamento prenotazioni...</LoadingContainer>
              ) : bookings.length === 0 ? (
                <EmptyState>
                  <EmptyIcon><CalendarIcon /></EmptyIcon>
                  <h3>Nessuna prenotazione ancora</h3>
                  <p>Esplora i nostri alloggi e prenota il tuo soggiorno perfetto!</p>
                  <ActionButton to="/accommodations" style={{ display: 'inline-flex', marginTop: '1rem' }}>
                    <HomeIcon />
                    Esplora Alloggi
                  </ActionButton>
                </EmptyState>
              ) : (
                bookings.map((booking) => (
                  <BookingCard key={booking.id}>
                    <BookingHeader>
                      <BookingTitle>
                        Prenotazione #{booking.id.slice(0, 8)}
                      </BookingTitle>
                      <BookingStatus status={booking.status}>
                        {booking.status === 'confirmed' && 'Confermata'}
                        {booking.status === 'pending' && 'In Attesa'}
                        {booking.status === 'cancelled' && 'Annullata'}
                        {booking.status === 'completed' && 'Completata'}
                      </BookingStatus>
                    </BookingHeader>
                    
                    <BookingDetails>
                      <InfoRow>
                        <InfoLabel>Check-in</InfoLabel>
                        <InfoValue>{new Date(booking.check_in).toLocaleDateString('it-IT')}</InfoValue>
                      </InfoRow>
                      
                      <InfoRow>
                        <InfoLabel>Check-out</InfoLabel>
                        <InfoValue>{new Date(booking.check_out).toLocaleDateString('it-IT')}</InfoValue>
                      </InfoRow>
                      
                      <InfoRow>
                        <InfoLabel>Ospiti</InfoLabel>
                        <InfoValue>{booking.guests_count}</InfoValue>
                      </InfoRow>
                      
                      <InfoRow>
                        <InfoLabel>Totale</InfoLabel>
                        <InfoValue>{formatPrice(booking.total_price)}</InfoValue>
                      </InfoRow>
                    </BookingDetails>
                  </BookingCard>
                ))
              )}
            </Card>
          </MainContent>
        </DashboardGrid>
      </DashboardContainer>
    </Layout>
  );
};

export default Dashboard; 